import type { Column } from '@shared/types/data-table'
import type { ReserveProfile } from '@features/reserves/types/reserve.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { createIdColumn, createTextColumn, createCustomColumn, createDateColumn } from './column-factories'
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge'
import { PROGRAM_OPTIONS } from '@shared/constants/billing-constants'

const MODEL_LABELS: Record<string, string> = {
  rolling: 'Rolling',
  fixed: 'Fixed',
  capped: 'Capped',
  'up-front': 'Up-front',
}

export const BILLING_RESERVE_COLUMNS: Record<string, Column<ReserveProfile>> = {
  id: createIdColumn<ReserveProfile>(),
  name: createTextColumn<ReserveProfile>(
    'name' as keyof ReserveProfile,
    'Name',
    160,
    'text-sm font-medium',
  ),
  status: {
    ...createCustomColumn<ReserveProfile>(
      'status',
      '',
      120,
      (profile: ReserveProfile) => (
        <AppStatusBadge entityType="billing-reserve" status={profile.status || 'unknown'} />
      ),
    ),
    header: '',
  },
  description: createTextColumn<ReserveProfile>(
    'description' as keyof ReserveProfile,
    'Description',
    220,
    'text-sm text-muted-foreground',
  ),
  model: createCustomColumn<ReserveProfile>(
    'model',
    'Model',
    120,
    (profile: ReserveProfile) => (
      <span className="text-sm text-muted-foreground">
        {MODEL_LABELS[profile.model] || profile.model}
      </span>
    ),
  ),
  reservePercent: createCustomColumn<ReserveProfile>(
    'reservePercent',
    'Reserve %',
    90,
    (profile: ReserveProfile) => (
      <span className="text-sm text-muted-foreground tabular-nums">
        {profile.reservePercent > 0 ? `${profile.reservePercent}%` : '\u2014'}
      </span>
    ),
  ),
  program: createCustomColumn<ReserveProfile>(
    'program',
    'Program',
    140,
    (profile: ReserveProfile) => {
      const label = PROGRAM_OPTIONS.find((p) => p.value === profile.program)?.label || profile.program
      return <span className="text-sm text-muted-foreground">{label}</span>
    },
  ),
  merchant: createTextColumn<ReserveProfile>(
    'merchant' as keyof ReserveProfile,
    'Merchant',
    150,
    'text-sm text-muted-foreground',
  ),
  createdDate: createDateColumn<ReserveProfile>(
    'createdDate' as keyof ReserveProfile,
    'Created',
    140,
    false,
  ),
  effectiveDate: createDateColumn<ReserveProfile>(
    'effectiveDate' as keyof ReserveProfile,
    'Effective',
    140,
    false,
  ),
  updatedDate: createDateColumn<ReserveProfile>(
    'updatedDate' as keyof ReserveProfile,
    'Updated',
    140,
    false,
  ),
}

const BILLING_RESERVE_COLUMN_ORDER = ['id', 'name', 'status', 'description', 'model', 'reservePercent', 'program', 'merchant', 'createdDate', 'effectiveDate', 'updatedDate'] as const

const BILLING_RESERVE_STATUS_CONFIG: Record<string, readonly string[]> = {
  all: BILLING_RESERVE_COLUMN_ORDER,
  active: BILLING_RESERVE_COLUMN_ORDER,
  draft: BILLING_RESERVE_COLUMN_ORDER,
  archived: BILLING_RESERVE_COLUMN_ORDER,
}

export const getBillingReserveColumns = (status: string = 'all'): Column<ReserveProfile>[] => {
  const keys =
    BILLING_RESERVE_STATUS_CONFIG[status as keyof typeof BILLING_RESERVE_STATUS_CONFIG] ||
    BILLING_RESERVE_STATUS_CONFIG.all
  return keys.map((key) => BILLING_RESERVE_COLUMNS[key]).filter(Boolean)
}

export const getBillingReserveColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(
    BILLING_RESERVE_STATUS_CONFIG[status as keyof typeof BILLING_RESERVE_STATUS_CONFIG] ||
    BILLING_RESERVE_STATUS_CONFIG.all
  )
  return Object.entries(BILLING_RESERVE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['id', 'name', 'status'].includes(key),
    order: index,
  }))
}
